import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { uploadProfileImage, deleteProfileImage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
    User, Camera, X, Plus, Heart, Dumbbell, Cake, Save,
    ArrowLeft, Mail, Calendar, Ruler, Scale
} from 'lucide-react';

export default function Profile() {
    const { user, updateProfile } = useAuth();
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);
    
    const [profileForm, setProfileForm] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        birth_date: user?.birth_date || '',
        gender: user?.gender || '',
        weight: user?.weight || '',
        height: user?.height || '',
        fitness_level: user?.fitness_level || 'beginner',
        hobbies: user?.hobbies || [],
        known_conditions: user?.known_conditions || [],
    });
    
    const [newHobby, setNewHobby] = useState('');
    const [newCondition, setNewCondition] = useState('');

    const handleSave = async () => {
        setLoading(true);
        try {
            const fullName = `${profileForm.first_name} ${profileForm.last_name}`.trim();
            await updateProfile({
                name: fullName || user?.name,
                first_name: profileForm.first_name || null,
                last_name: profileForm.last_name || null,
                birth_date: profileForm.birth_date || null,
                gender: profileForm.gender || null,
                weight: profileForm.weight ? parseFloat(profileForm.weight) : null,
                height: profileForm.height ? parseFloat(profileForm.height) : null,
                fitness_level: profileForm.fitness_level,
                hobbies: profileForm.hobbies,
                known_conditions: profileForm.known_conditions,
            });
            toast.success(language === 'de' ? 'Profil gespeichert!' : 'Profile saved!');
        } catch (error) {
            toast.error(t('error_saving'));
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error(language === 'de' ? 'Bild zu groß (max 5MB)' : 'Image too large (max 5MB)');
            return;
        }
        
        setUploadingImage(true);
        try {
            const response = await uploadProfileImage(file);
            await updateProfile({ profile_image: response.data.profile_image });
            toast.success(language === 'de' ? 'Profilbild aktualisiert!' : 'Profile image updated!');
        } catch (error) {
            toast.error(language === 'de' ? 'Upload fehlgeschlagen' : 'Upload failed');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDeleteImage = async () => {
        try {
            await deleteProfileImage();
            await updateProfile({ profile_image: null });
            toast.success(language === 'de' ? 'Profilbild entfernt' : 'Profile image removed');
        } catch (error) {
            toast.error(t('error_deleting'));
        }
    };

    const addHobby = () => {
        if (newHobby.trim() && !profileForm.hobbies.includes(newHobby.trim())) {
            setProfileForm({ ...profileForm, hobbies: [...profileForm.hobbies, newHobby.trim()] });
            setNewHobby('');
        }
    };

    const removeHobby = (hobby) => {
        setProfileForm({ ...profileForm, hobbies: profileForm.hobbies.filter(h => h !== hobby) });
    };

    const addCondition = () => {
        if (newCondition.trim() && !profileForm.known_conditions.includes(newCondition.trim())) {
            setProfileForm({ ...profileForm, known_conditions: [...profileForm.known_conditions, newCondition.trim()] });
            setNewCondition('');
        }
    };

    const removeCondition = (condition) => {
        setProfileForm({ ...profileForm, known_conditions: profileForm.known_conditions.filter(c => c !== condition) });
    };

    const calculateBMI = () => {
        if (profileForm.weight && profileForm.height) {
            const heightM = parseFloat(profileForm.height) / 100;
            const bmi = parseFloat(profileForm.weight) / (heightM * heightM);
            return bmi.toFixed(1);
        }
        return null;
    };

    const calculateAge = () => {
        if (profileForm.birth_date) {
            const birth = new Date(profileForm.birth_date);
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const m = today.getMonth() - birth.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                age--;
            }
            return age;
        }
        return null;
    };

    const profileImageUrl = user?.profile_image 
        ? (user.profile_image.startsWith('data:') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`)
        : null;

    const displayName = profileForm.first_name || profileForm.last_name 
        ? `${profileForm.first_name} ${profileForm.last_name}`.trim()
        : user?.name || 'User';

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="profile-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto space-y-6"
            >
                {/* Back Button */}
                <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/')}
                    className="gap-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {language === 'de' ? 'Zurück' : 'Back'}
                </Button>

                {/* Profile Header Card */}
                <Card className="overflow-hidden">
                    <div className="h-24 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" />
                    <CardContent className="relative pt-0 pb-6">
                        {/* Profile Image */}
                        <div className="flex flex-col items-center -mt-16">
                            <div className="relative">
                                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center overflow-hidden border-4 border-background shadow-xl">
                                    {profileImageUrl ? (
                                        <img 
                                            src={profileImageUrl} 
                                            alt="Profile" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-5xl font-bold text-foreground">
                                            {(profileForm.first_name?.[0] || user?.name?.[0] || 'U').toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageUpload}
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                />
                                <Button
                                    size="icon"
                                    className="absolute bottom-1 right-1 rounded-full h-10 w-10 shadow-lg"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingImage}
                                >
                                    <Camera className="w-5 h-5" />
                                </Button>
                            </div>
                            
                            <h1 className="mt-4 text-2xl font-bold">{displayName}</h1>
                            <p className="text-muted-foreground flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {user?.email}
                            </p>
                            
                            {profileImageUrl && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="mt-2 text-destructive"
                                    onClick={handleDeleteImage}
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    {language === 'de' ? 'Bild entfernen' : 'Remove image'}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Personal Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <User className="w-5 h-5 text-violet-500" />
                            {language === 'de' ? 'Persönliche Daten' : 'Personal Information'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Name Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{language === 'de' ? 'Vorname' : 'First Name'}</Label>
                                <Input
                                    value={profileForm.first_name}
                                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                                    placeholder="Max"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{language === 'de' ? 'Nachname' : 'Last Name'}</Label>
                                <Input
                                    value={profileForm.last_name}
                                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                                    placeholder="Mustermann"
                                />
                            </div>
                        </div>

                        {/* Birth Date */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Cake className="w-4 h-4" />
                                {language === 'de' ? 'Geburtsdatum' : 'Date of Birth'}
                            </Label>
                            <Input
                                type="date"
                                value={profileForm.birth_date}
                                onChange={(e) => setProfileForm({ ...profileForm, birth_date: e.target.value })}
                            />
                            {calculateAge() && (
                                <p className="text-sm text-muted-foreground">
                                    {language === 'de' ? `${calculateAge()} Jahre alt` : `${calculateAge()} years old`}
                                </p>
                            )}
                        </div>

                        {/* Gender */}
                        <div className="space-y-2">
                            <Label>{t('gender')}</Label>
                            <Select 
                                value={profileForm.gender} 
                                onValueChange={(value) => setProfileForm({ ...profileForm, gender: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={language === 'de' ? 'Auswählen' : 'Select'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="male">{t('male')}</SelectItem>
                                    <SelectItem value="female">{t('female')}</SelectItem>
                                    <SelectItem value="diverse">{t('diverse')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Body Stats */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Scale className="w-5 h-5 text-emerald-500" />
                            {language === 'de' ? 'Körperdaten' : 'Body Stats'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1">
                                    <Scale className="w-3 h-3" />
                                    {t('weight_kg')}
                                </Label>
                                <Input
                                    type="number"
                                    value={profileForm.weight}
                                    onChange={(e) => setProfileForm({ ...profileForm, weight: e.target.value })}
                                    placeholder="70"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1">
                                    <Ruler className="w-3 h-3" />
                                    {t('height_cm')}
                                </Label>
                                <Input
                                    type="number"
                                    value={profileForm.height}
                                    onChange={(e) => setProfileForm({ ...profileForm, height: e.target.value })}
                                    placeholder="175"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>BMI</Label>
                                <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center font-medium">
                                    {calculateBMI() || '-'}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Dumbbell className="w-4 h-4" />
                                {t('fitness_level')}
                            </Label>
                            <Select 
                                value={profileForm.fitness_level} 
                                onValueChange={(value) => setProfileForm({ ...profileForm, fitness_level: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="beginner">{t('beginner')}</SelectItem>
                                    <SelectItem value="intermediate">{t('intermediate')}</SelectItem>
                                    <SelectItem value="advanced">{t('advanced')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Hobbies */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">
                            {language === 'de' ? 'Hobbys & Interessen' : 'Hobbies & Interests'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'de' 
                                ? 'Hilft dem AI Coach, dich besser zu verstehen'
                                : 'Helps the AI Coach understand you better'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-2 min-h-[40px]">
                            {profileForm.hobbies.length === 0 && (
                                <p className="text-sm text-muted-foreground">
                                    {language === 'de' ? 'Noch keine Hobbys hinzugefügt' : 'No hobbies added yet'}
                                </p>
                            )}
                            {profileForm.hobbies.map((hobby) => (
                                <Badge key={hobby} variant="secondary" className="gap-1 pr-1 text-sm">
                                    {hobby}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4 hover:bg-transparent"
                                        onClick={() => removeHobby(hobby)}
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={newHobby}
                                onChange={(e) => setNewHobby(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHobby())}
                                placeholder={language === 'de' ? 'z.B. Yoga, Wandern, Kochen...' : 'e.g. Yoga, Hiking, Cooking...'}
                                className="flex-1"
                            />
                            <Button onClick={addHobby} size="icon" variant="outline">
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Known Conditions */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Heart className="w-5 h-5 text-rose-500" />
                            {language === 'de' ? 'Bekannte Beschwerden' : 'Known Conditions'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'de' 
                                ? 'Werden bei Empfehlungen berücksichtigt'
                                : 'Considered in recommendations'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-2 min-h-[40px]">
                            {profileForm.known_conditions.length === 0 && (
                                <p className="text-sm text-muted-foreground">
                                    {language === 'de' ? 'Keine Beschwerden hinterlegt' : 'No conditions added'}
                                </p>
                            )}
                            {profileForm.known_conditions.map((condition) => (
                                <Badge key={condition} variant="outline" className="gap-1 pr-1 text-sm border-rose-200 bg-rose-50 dark:bg-rose-900/20">
                                    {condition}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4 hover:bg-transparent"
                                        onClick={() => removeCondition(condition)}
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={newCondition}
                                onChange={(e) => setNewCondition(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCondition())}
                                placeholder={language === 'de' ? 'z.B. Migräne, Rückenschmerzen...' : 'e.g. Migraines, Back pain...'}
                                className="flex-1"
                            />
                            <Button onClick={addCondition} size="icon" variant="outline">
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Save Button */}
                <Button 
                    onClick={handleSave} 
                    disabled={loading} 
                    className="w-full gap-2 h-12 text-lg"
                    size="lg"
                >
                    <Save className="w-5 h-5" />
                    {loading 
                        ? (language === 'de' ? 'Speichern...' : 'Saving...') 
                        : (language === 'de' ? 'Profil speichern' : 'Save Profile')}
                </Button>
            </motion.div>
        </div>
    );
}
